import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import queryParamsUtils from '../../utils/queryParamsUtils';
import { useProductStore } from '../../context/ProductContext';
import { marpolRegulationToIconMapper, getMarpolRegulationAltText } from '../../utils/iconMapper';
import { getProductHistory } from '../../utils/productHistory';
import { ProductListItem } from '../../types/ProductTypes';
import useProductQuery from '../../api/useProductQuery';
import {
    ProductsListContainer,
    ProductsHeader,
    ViewToggle,
    ViewButton,
    LoadingContainer,
    LoadingSpinner,
    SearchSection,
    SearchInputWrapper,
    SearchInput,
    EmptyState,
    ProductsListView,
    ListHeader,
    ListRowsContainer,
    HeaderCell,
    ListRow,
    ListCell,
    ProductsGrid,
    ProductCard,
    ProductHeader,
    ProductName,
    ProductRegulation,
    ProductDetails,
    ProductCategory,
    ProductDescription,
    ProductCodes,
    CodeBadge,
    ProductFooter,
    ProductDate,
    PaginationContainer,
    PaginationButton,
    PaginationInfo,
    SearchLoadingIndicator,
} from './ProductsList.styles';

type ViewMode = 'list' | 'card';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

interface ProductsListProps {
    showPreviouslyVisited?: boolean;
}

const ProductsList: React.FC<ProductsListProps> = ({ showPreviouslyVisited = false }) => {
    const { productListItems, setProductListItems, pagination, setPagination, setIsProductListRetrieved } = useProductStore();
    const { queryProducts, isLoading } = useProductQuery();
    const navigate = useNavigate();
    const { setQueryParamProductId } = queryParamsUtils();
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [scrollHeight, setScrollHeight] = useState<number>(0);
    const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1); // Reset to first page on new search
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Fetch products when search term or page changes
    const fetchProducts = useCallback(async () => {
        try {
            const result = await queryProducts({
                search: debouncedSearchTerm || undefined,
                page: currentPage,
                pageSize: DEFAULT_PAGE_SIZE,
                includeInactive: false,
            });

            setProductListItems(result.items);
            setPagination({
                totalCount: result.totalCount,
                pageNumber: result.pageNumber,
                pageSize: result.pageSize,
                totalPages: Math.ceil(result.totalCount / result.pageSize),
            });
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
        } catch (error) {
            console.error('[ProductsList] Error fetching products:', error);
            setProductListItems([]);
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
        }
    }, [debouncedSearchTerm, currentPage, queryProducts, setProductListItems, setPagination, setIsProductListRetrieved]);

    // Initial load and search/page change handler
    useEffect(() => {
        // Skip if showing previously visited and no search
        if (showPreviouslyVisited && !debouncedSearchTerm) {
            setIsProductListRetrieved(true);
            setHasInitialLoad(true);
            return;
        }

        fetchProducts();
    }, [debouncedSearchTerm, currentPage, showPreviouslyVisited, fetchProducts, setIsProductListRetrieved]);

    // Calculate scroll container height
    useEffect(() => {
        const calculateHeight = () => {
            if (!containerRef.current || !headerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const headerRect = headerRef.current.getBoundingClientRect();
            const remainingHeight = window.innerHeight - containerRect.top - headerRect.height - 32; // 32px for padding

            setScrollHeight(Math.max(remainingHeight, 200)); // Minimum 200px height
        };

        calculateHeight();

        // Recalculate on window resize
        const resizeObserver = new ResizeObserver(() => {
            calculateHeight();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', calculateHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', calculateHeight);
        };
    }, []);

    const getDisplayedProducts = (): ProductListItem[] => {
        // If showPreviouslyVisited is true and no search is active, show history
        if (showPreviouslyVisited && !searchTerm.trim()) {
            return getProductHistory();
        }

        return productListItems;
    };

    const displayedProducts = getDisplayedProducts();
    const showPagination = !showPreviouslyVisited || searchTerm.trim();

    // Determine header title based on context
    const headerTitle = showPreviouslyVisited && !searchTerm.trim() ? 'Previously Visited' : 'Products';
    const displayCount = showPreviouslyVisited && !searchTerm.trim() ? displayedProducts.length : pagination.totalCount;

    const handleProductClick = (productId: string | number) => {
        // Navigate to product page
        navigate('/product');
        // Set the product ID as a query parameter
        setQueryParamProductId(productId.toString());
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <ProductsListContainer ref={containerRef}>
            <ProductsHeader ref={headerRef}>
                <h2>
                    {headerTitle} ({displayCount})
                </h2>
                <ViewToggle>
                    <ViewButton $active={viewMode === 'list'} onClick={() => setViewMode('list')} title="List View">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
                        </svg>
                    </ViewButton>
                    <ViewButton $active={viewMode === 'card'} onClick={() => setViewMode('card')} title="Card View">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
                        </svg>
                    </ViewButton>
                </ViewToggle>
            </ProductsHeader>

            {/* Search Section */}
            <SearchSection>
                <SearchInputWrapper>
                    <SearchInput
                        type="text"
                        placeholder="Search products by trade name or synonym..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {isLoading && <SearchLoadingIndicator />}
                </SearchInputWrapper>
            </SearchSection>

            {!hasInitialLoad ? (
                <LoadingContainer>
                    <LoadingSpinner />
                    <p>Loading products...</p>
                </LoadingContainer>
            ) : displayedProducts?.length === 0 ? (
                <EmptyState>
                    <p>No products found.</p>
                </EmptyState>
            ) : viewMode === 'list' ? (
                <ProductsListView>
                    <ListHeader>
                        <HeaderCell className="icon-header"></HeaderCell>
                        <HeaderCell>Trade Name</HeaderCell>
                        <HeaderCell>Product Name (IBC)</HeaderCell>
                        <HeaderCell>Charterer</HeaderCell>
                    </ListHeader>
                    <ListRowsContainer style={{ height: scrollHeight - (showPagination && pagination.totalPages > 1 ? 50 : 0) }}>
                        {displayedProducts.map((product: ProductListItem, index: number) => {
                            const marpolRegNum = parseInt(product.marpolRegulation || '0', 10);
                            return (
                                <ListRow key={product.id || index} $clickable onClick={() => handleProductClick(product.id)}>
                                    <ListCell className="icon-cell">
                                        <img
                                            src={marpolRegulationToIconMapper(marpolRegNum)}
                                            alt={getMarpolRegulationAltText(marpolRegNum)}
                                            className="marpol-icon"
                                        />
                                    </ListCell>
                                    <ListCell className="product-name-cell">
                                        <span className="product-name">{product.tradeName || ''}</span>
                                    </ListCell>
                                    <ListCell>{product.categoryName || 'Unknown'}</ListCell>
                                    <ListCell>{product.chartererName || '-'}</ListCell>
                                </ListRow>
                            );
                        })}
                    </ListRowsContainer>
                    {showPagination && pagination.totalPages > 1 && (
                        <PaginationContainer>
                            <PaginationButton
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1 || isLoading}
                                $disabled={currentPage === 1 || isLoading}
                            >
                                First
                            </PaginationButton>
                            <PaginationButton
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || isLoading}
                                $disabled={currentPage === 1 || isLoading}
                            >
                                Previous
                            </PaginationButton>
                            <PaginationInfo>
                                Page {currentPage} of {pagination.totalPages}
                            </PaginationInfo>
                            <PaginationButton
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages || isLoading}
                                $disabled={currentPage === pagination.totalPages || isLoading}
                            >
                                Next
                            </PaginationButton>
                            <PaginationButton
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={currentPage === pagination.totalPages || isLoading}
                                $disabled={currentPage === pagination.totalPages || isLoading}
                            >
                                Last
                            </PaginationButton>
                        </PaginationContainer>
                    )}
                </ProductsListView>
            ) : (
                <>
                    <ProductsGrid style={{ height: scrollHeight - (showPagination && pagination.totalPages > 1 ? 50 : 0) }}>
                        {displayedProducts.map((product: ProductListItem, index: number) => (
                            <ProductCard key={product.id || index} $clickable onClick={() => handleProductClick(product.id)}>
                                <ProductHeader>
                                    <ProductName>{product.tradeName || ''}</ProductName>
                                    {product.marpolRegulation && <ProductRegulation>{product.marpolRegulation}</ProductRegulation>}
                                </ProductHeader>

                                <ProductDetails>
                                    <ProductCategory>
                                        <strong>Category:</strong> {product.categoryName || 'Unknown'}
                                    </ProductCategory>

                                    {product.chartererName && (
                                        <ProductDescription>
                                            <strong>Charterer:</strong> {product.chartererName}
                                        </ProductDescription>
                                    )}

                                    <ProductCodes>{product.id && <CodeBadge>ID: {product.id}</CodeBadge>}</ProductCodes>
                                </ProductDetails>

                                <ProductFooter>
                                    <ProductDate>Status: {product.isActive ? 'Active' : 'Inactive'}</ProductDate>
                                </ProductFooter>
                            </ProductCard>
                        ))}
                    </ProductsGrid>
                    {showPagination && pagination.totalPages > 1 && (
                        <PaginationContainer>
                            <PaginationButton
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1 || isLoading}
                                $disabled={currentPage === 1 || isLoading}
                            >
                                First
                            </PaginationButton>
                            <PaginationButton
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || isLoading}
                                $disabled={currentPage === 1 || isLoading}
                            >
                                Previous
                            </PaginationButton>
                            <PaginationInfo>
                                Page {currentPage} of {pagination.totalPages}
                            </PaginationInfo>
                            <PaginationButton
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages || isLoading}
                                $disabled={currentPage === pagination.totalPages || isLoading}
                            >
                                Next
                            </PaginationButton>
                            <PaginationButton
                                onClick={() => handlePageChange(pagination.totalPages)}
                                disabled={currentPage === pagination.totalPages || isLoading}
                                $disabled={currentPage === pagination.totalPages || isLoading}
                            >
                                Last
                            </PaginationButton>
                        </PaginationContainer>
                    )}
                </>
            )}
        </ProductsListContainer>
    );
};

export default ProductsList;
